import {
  MouseEventHandler,
  useState,
  useRef,
  useCallback,
  useEffect,
  VFC,
  KeyboardEvent,
  useMemo,
} from 'react';
import * as Styled from './bar.styles';

interface BarProps {
  onChange?: (value: number) => void;
  width?: number;
  value: number;
  minValue?: number;
  maxValue?: number;
  numSteps?: number;
  handleSize?: number;
  color?: string;
}

// Maps a value within a range to a new range.
function linearRemap(
  value: number,
  minValue: number,
  mavValue: number,
  newMin = 0,
  newMax = 100
): number {
  return (
    ((newMax - newMin) * (value - minValue)) / (mavValue - minValue) + newMin
  );
}

export const Bar: VFC<BarProps> = ({
  onChange,
  width = 200,
  value,
  minValue = 0,
  maxValue = 100,
  numSteps = 10,
  handleSize = 16,
  color = 'red',
}) => {
  // The size of a step when moving the bar with a keyboard.
  const stepSize = useMemo(() => {
    return (maxValue - minValue) / numSteps;
  }, [minValue, maxValue, numSteps]);

  const [handleLeft, setHandleLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleGrabed: MouseEventHandler<HTMLDivElement> = () => {
    window.addEventListener('mousemove', handleMoved);
    window.addEventListener('mouseup', handleReleased);
  };

  const calculateHandleLeft = useCallback(
    (curValue: number) => {
      if (!containerRef.current) return;

      const valuePercentage = linearRemap(curValue, minValue, maxValue);

      const containerWidth = containerRef.current.clientWidth;

      let newLeft =
        linearRemap(valuePercentage, 0, 100, 0, containerWidth) -
        handleSize / 2;

      if (valuePercentage >= 100) {
        newLeft -= handleSize / 2;
      } else if (valuePercentage <= 0) {
        newLeft = 0;
      }

      return newLeft;
    },
    [maxValue, minValue, handleSize]
  );

  useEffect(() => {
    setHandleLeft(calculateHandleLeft(value) || 0);
  }, [calculateHandleLeft, value]);

  const handleMoved = useCallback(
    (event: MouseEvent) => {
      if (!containerRef.current || !onChange) return;

      const containerLeftBound = containerRef.current.getBoundingClientRect().x;
      const containerWidth = containerRef.current.clientWidth;
      const containerRightBound = containerWidth + containerLeftBound;

      const hX = event.clientX;
      const handleLeftBound = hX - handleSize / 2;
      const handleRightBound = hX + handleSize / 2;

      let value;

      if (handleLeftBound <= containerLeftBound) {
        value = 0;
      } else if (handleRightBound >= containerRightBound) {
        value = 100;
      } else {
        value = ((hX - containerLeftBound) / containerWidth) * 100;
      }

      onChange(value);
    },
    [handleSize, onChange]
  );

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (!onChange) return;

      switch (e.key) {
        case 'ArrowLeft':
          onChange(Math.max(value - stepSize, minValue));
          break;
        case 'ArrowRight':
          onChange(Math.min(value + stepSize, maxValue));
          break;
        default:
          return;
      }
    },
    [maxValue, minValue, onChange, stepSize, value]
  );

  const handleReleased = (): void => {
    window.removeEventListener('mousemove', handleMoved);
    window.removeEventListener('mouseup', handleReleased);
  };

  return (
    <Styled.Wrapper>
      <Styled.Container ref={containerRef}>
        <Styled.Handle
          onKeyDown={handleKeyPress}
          tabIndex={0}
          data-testid="handle"
          barWidth={width}
          left={handleLeft}
          handleSize={handleSize}
          onMouseDown={handleGrabed}
          color={color}
        />
      </Styled.Container>
    </Styled.Wrapper>
  );
};
